import React from 'react';

import { CharterScheduleItem } from 'Modules/Charter/Types/CharterScheduleItem';
import { GenericStaffCollection } from 'Views/Components/_HumanWritten/StaffTables/GenericStaffCollection';
import { SortableHeaderButton } from 'Views/Components/_HumanWritten/StaffTables/SortableHeaderButton';
import { CharterRender } from './CharterRender';
import { CharterColumnKeys } from './CharterColumnKeys';
import { StaffChartersFilters, CharterOrderByOption, useStaffChartersStore } from './StaffChartersFilterStore';

export interface StaffChartersCollectionProps {
	charters: CharterScheduleItem[] | null;
	totalCharterCount: number;
	perPage: number;
	filters: StaffChartersFilters;
	setFilters: (newFilters: StaffChartersFilters) => void;
	isLoading: boolean;
	lastUpdated: Date;
	/** On a customer's page: the customer column would repeat the same person down every row. */
	hideCustomerColumn?: boolean;
	/** The reader is the customer, not staff — see `StaffChartersListOuter`. */
	viewerIsCustomer?: boolean;
}

/**
 * The charters table itself — the rows, the headers and the pager.
 *
 * Server-paged and server-sorted, because the staff-wide copy is read against every charter ever
 * taken. A customer's copy goes through the same read, narrowed to them.
 *
 * Nothing here is done to a set of charters — no export, no bulk action — so the rows carry no
 * checkbox and there is no selection to hold. `GenericStaffCollection` still wants the pair, so it
 * is given an empty set and a no-op.
 */
export function StaffChartersCollection({
	charters,
	totalCharterCount,
	perPage,
	filters,
	setFilters,
	isLoading,
	lastUpdated,
	hideCustomerColumn,
	viewerIsCustomer,
}: StaffChartersCollectionProps) {
	const { visibleColumns } = useStaffChartersStore();
	const visibleKeys = new Set(
		visibleColumns
			.filter(c => c.visible)
			.filter(c => !(hideCustomerColumn && c.key === CharterColumnKeys.CUSTOMER))
			.map(c => c.key),
	);

	// Re-sorting goes back to page one: the row that was on page 4 under the old sort is somewhere
	// else entirely under the new one, so holding the page number would land the reader nowhere.
	const handleSort = (sortField: string, desc: boolean) =>
		setFilters({
			...filters,
			orderBy: sortField,
			orderByDescending: desc,
			pageNo: 0,
		});

	const sortableHeader = (text: string, field: CharterOrderByOption) => (
		<SortableHeaderButton
			text={text}
			orderByField={field}
			currentOrderBy={filters.orderBy}
			isDescending={filters.orderByDescending}
			onSort={handleSort}
		/>
	);

	return (
		<GenericStaffCollection
			items={charters}
			totalItemCount={totalCharterCount}
			perPage={perPage}
			currentPage={filters.pageNo}
			onPageChange={page => setFilters({ ...filters, pageNo: page })}
			isLoading={isLoading}
			selectedItemIds={new Set<string>()}
			setSelectedItemIds={() => {}}
			lastUpdated={lastUpdated}
			className="staff__table staff__table__charters"
			testId="staff-charters-table"
			renderRow={charter => (
				<CharterRender
					key={charter.id}
					charter={charter}
					visibleColumns={visibleKeys}
					viewerIsCustomer={viewerIsCustomer}
				/>
			)}
			columns={(
				[
					{
						key: CharterColumnKeys.REFERENCE,
						label: 'Charter ID',
						headerComponent: sortableHeader('Charter ID', CharterOrderByOption.REFERENCE),
					},
					{
						key: CharterColumnKeys.NAME,
						label: 'Charter',
						headerComponent: sortableHeader('Charter', CharterOrderByOption.NAME),
					},
					{
						key: CharterColumnKeys.CUSTOMER,
						label: 'Customer',
						headerComponent: sortableHeader('Customer', CharterOrderByOption.CUSTOMER),
					},
					{
						key: CharterColumnKeys.FERRY,
						label: 'Ferry',
						headerComponent: sortableHeader('Ferry', CharterOrderByOption.FERRY),
					},
					{
						key: CharterColumnKeys.START_DATE,
						label: 'Ferry held from',
						headerComponent: sortableHeader('Ferry held from', CharterOrderByOption.START_DATE),
					},
					{
						key: CharterColumnKeys.END_DATE,
						label: 'Ferry held until',
						headerComponent: sortableHeader('Ferry held until', CharterOrderByOption.END_DATE),
					},
					{
						key: CharterColumnKeys.PRICE,
						label: 'Price',
						headerComponent: sortableHeader('Price', CharterOrderByOption.PRICE),
					},
					{
						key: CharterColumnKeys.STATUS,
						label: 'Status',
						headerComponent: sortableHeader('Status', CharterOrderByOption.STATUS),
					},
					// Payment state is derived from the transaction's status rather than stored, so
					// there is no column to sort by — the header is plain.
					{ key: CharterColumnKeys.PAYMENT, label: 'Payment' },
					{
						key: CharterColumnKeys.INVOICE_NUMBER,
						label: 'Invoice number',
						headerComponent: sortableHeader('Invoice number', CharterOrderByOption.INVOICE_NUMBER),
					},
				] as { key: string; label: string; headerComponent?: React.ReactNode }[]
			).filter(col => visibleKeys.has(col.key))}
			emptyMessage="No charters found"
			loadingMessage="Loading charters"
		/>
	);
}
